"use client";

import { Home, type LucideIcon, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useSyncExternalStore } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePathname } from "@/i18n/use-pathname";
import { useRouter } from "@/i18n/use-router";
import { cn } from "@/lib/utils";
import { appOfPath, appTabsKey, type AppTabsState, appTabsStore, closeAppTab, HOME_TAB_ID, openApp, openAppTab, pruneAppTabs, syncAppTabs } from "./app-tabs-store";
import { APP_MODULES } from "@/components/app-switcher/app-modules";

// The colours of the bar of tabs at the very top. The bar and its words and icons come from two variables written with the app colours (--top-bar, set in Settings > App brand, and --top-bar-foreground, white or black chosen to read on it); the current tab follows the header just below it (the theme's brand colour and its own foreground), so the tab and the header
// look like one piece. One place to change them. (Class names are written out in full so Tailwind can find them.)
const APP_TAB_COLOURS = {
  bar: "bg-(--top-bar) text-(--top-bar-foreground)",
  active: "rounded-t-lg bg-brand font-bold text-brand-foreground",
  idle: "text-[color-mix(in_srgb,var(--top-bar-foreground)_85%,transparent)] hover:bg-[color-mix(in_srgb,var(--top-bar-foreground)_15%,transparent)] hover:text-(--top-bar-foreground)",
  close: "opacity-85 hover:bg-[color-mix(in_srgb,var(--top-bar-foreground)_25%,transparent)] hover:opacity-100 focus-visible:outline-2 focus-visible:outline-current",
  focus: "focus-visible:outline-2 focus-visible:outline-(--top-bar-foreground)",
} as const;

type Props = { name: string; icon: LucideIcon; active: boolean; closeLabel?: string; onChoose: () => void; onClose?: () => void };

/** One tab of the bar at the very top: the icon and name as a button (the current tab is darker, like a browser tab) and, for a tab that can be closed, a close button; the permanent home tab has none. Words and icons in the colour that reads on the bar, with a clear focus ring. */
function AppTabItem({ name, icon: Icon, active, closeLabel, onChoose, onClose }: Props) {
  return (
    <li className={cn("flex items-center rounded-t-lg", onClose ? "min-w-28 max-w-52 flex-1 basis-0" : "shrink-0", active ? APP_TAB_COLOURS.active : APP_TAB_COLOURS.idle)}>
      <button type="button" onClick={onChoose} aria-current={active ? "page" : undefined} className={cn("flex min-w-0 flex-1 items-center gap-2 py-1 ps-3 text-sm", onClose ? "pe-1" : "pe-3", APP_TAB_COLOURS.focus)}>
        <Icon aria-hidden className="size-4 shrink-0" /><span className="truncate">{name}</span>
      </button>
      {onClose && <button type="button" onClick={onClose} aria-label={closeLabel} className={cn("me-1 rounded-full p-1", APP_TAB_COLOURS.close)}><X aria-hidden className="size-3.5" /></button>}
    </li>
  );
}

/** The function that goes to an app through the tabs (used by the app switcher and the links to apps): when the app is already open in a tab that tab becomes current and the person goes back to the page it was on, otherwise a new tab opens on the app's home page. */
export function useOpenApp(): (appId: string) => void {
  const router = useRouter();
  return (appId) => {
    const app = APP_MODULES.find((m) => m.id === appId);
    if (!app) return;
    const store = appTabsStore(appTabsKey());
    const out = openApp(store.get(), appId, app.href, crypto.randomUUID());
    store.set(out.state);
    router.push(out.go);
  };
}

const INITIAL: AppTabsState = { tabs: [], active: HOME_TAB_ID };

/** The tabs of the bar at the very top, kept in the browser's session storage: they agree with the page shown (a page of an app makes that app's tab current, opening one when none is open; the main page makes the home tab current), `newTab` opens one more tab on an app's home page, `choose` goes to a tab's page
 *  (the home tab goes to the main page) and `close` closes a tab (and goes to its neighbour when it was the current one). `apps` are the ones the person may open. */
function useAppTabs(appIds: readonly string[]) {
  const store = appTabsStore(appTabsKey());
  const state = useSyncExternalStore(store.subscribe, store.get, () => INITIAL);
  const pathname = usePathname();
  const router = useRouter();
  useEffect(() => {
    const appId = appOfPath(pathname, APP_MODULES);
    store.set(syncAppTabs(store.get(), { appId, home: pathname === "/", href: `${pathname}${window.location.search}` }, crypto.randomUUID()));
  }, [pathname, store]);
  useEffect(() => { const kept = pruneAppTabs(store.get(), appIds); if (kept !== store.get()) store.set(kept); }, [appIds, store]);
  const go = (next: AppTabsState) => { store.set(next); router.push(next.active === HOME_TAB_ID ? "/" : (next.tabs.find((t) => t.id === next.active)?.href ?? "/")); };
  return {
    state, apps: APP_MODULES.filter((m) => appIds.includes(m.id)), openApp: useOpenApp(),
    newTab: (appId: string, href: string) => go(openAppTab(state, crypto.randomUUID(), appId, href)),
    choose: (id: string) => go({ ...state, active: id }),
    close: (id: string) => { const next = closeAppTab(state, id); if (state.active === id) go(next); else store.set(next); },
  };
}

/** The bar at the very top of the site for everyone signed in, above the header: first the permanent home tab (the main page), then one tab for each app open at the same time (Support here, My account there). Choosing a tab goes back to the page it was on; going to an app by any link or the app switcher goes to its tab when it is open;
 *  the + button opens one more tab on any app the person can open; a tab closes with its ×. Its stacking level is below the windows and side panels (they open above it and dim it, never under it). The bar has the colour chosen in Settings > App brand, the current tab follows the header, and the tabs come back after a reload (session storage). `appIds` are the apps the person may open. */
export function AppTabsBar({ appIds }: { appIds: string[] }) {
  const t = useTranslations("appTabs");
  const names = useTranslations("auth.modules");
  const { state, apps, newTab, choose, close } = useAppTabs(appIds);
  return (
    <nav data-app-tabs aria-label={t("label")} className={cn("sticky top-[var(--banner-h,0px)] z-40 flex h-9 shrink-0 items-stretch", APP_TAB_COLOURS.bar)}>
      <ul className="flex min-w-0 flex-1 gap-0.5 overflow-x-auto overflow-y-hidden">
        <AppTabItem name={t("home")} icon={Home} active={state.active === HOME_TAB_ID} onChoose={() => choose(HOME_TAB_ID)} />
        {state.tabs.map((tab) => {
          const app = apps.find((a) => a.id === tab.appId);
          return app && <AppTabItem key={tab.id} name={names(app.id)} icon={app.icon} active={tab.id === state.active} closeLabel={t("close", { name: names(app.id) })} onChoose={() => choose(tab.id)} onClose={() => close(tab.id)} />;
        })}
      </ul>
      <DropdownMenu>
        <DropdownMenuTrigger render={<button type="button" aria-label={t("add")} title={t("add")} className={cn("mx-1 shrink-0 self-center rounded-md p-1.5", APP_TAB_COLOURS.idle, APP_TAB_COLOURS.focus)} />}><Plus aria-hidden className="size-4" /></DropdownMenuTrigger>
        <DropdownMenuContent align="end">{apps.map((a) => <DropdownMenuItem key={a.id} onClick={() => newTab(a.id, a.href)}><a.icon aria-hidden />{names(a.id)}</DropdownMenuItem>)}</DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
